import { forgotPasswordSchema } from "@frontend-shared/schema/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { useForgotPassword } from "@/api/auth/useForgotPassword";
import { FormSubmitButton } from "@/components/FormSubmitButton";
import { Logo } from "@/components/Logo";
import { StyledInput } from "@/components/StyledInput";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useOnPremise } from "@/hooks/use-on-premise";
import { cn } from "@/lib/utils";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

import { CenterCardLayout } from "./CenterCardLayout";
import { AUTH_CONTROL_HEIGHT_CLASS } from "./constants/auth-controls";

const ForgotPassword = () => {
  const { t } = useTranslation("auth");
  useDocumentTitle(t("page_title"));
  const { isOnPremise } = useOnPremise();
  const forgotPasswordMutation = useForgotPassword();

  const form = useForm({
    ...FORM_VALIDATION_OPTIONS,
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(forgotPasswordSchema(t)),
  });

  const handleSubmit = form.handleSubmit((values) => {
    forgotPasswordMutation.mutate(values);
  });

  const resolveTitle = useMemo(() => {
    if (isOnPremise) {
      return (
        <Trans
          t={t}
          i18nKey={"sign_in.forgot_password"}
          components={{
            Highlighted: <span className="text-highlighted" />,
          }}
        />
      );
    }

    if (forgotPasswordMutation.isSuccess) {
      return (
        <Trans
          t={t}
          i18nKey={"forgot_password.success_title"}
          components={{
            Highlighted: <span className="text-highlighted" />,
          }}
        />
      );
    }

    return (
      <Trans
        t={t}
        i18nKey={"forgot_password.title"}
        components={{
          Highlighted: <span className="text-highlighted" />,
        }}
      />
    );
  }, [isOnPremise, forgotPasswordMutation.isSuccess, t]);

  return (
    <CenterCardLayout>
      <div className="flex flex-col justify-center">
        <Logo
          wrapperClassName="flex justify-center"
          className="h-logo-height-auth w-auto"
        />
        <h1
          className="text-xl md:text-2xl lg:text-3xl font-semibold text-center mt-3 md:mt-5"
          data-testid="forgot-password_title"
        >
          {resolveTitle}
        </h1>
        {!isOnPremise && (
          <p
            className="text-center text-foreground-secondary mt-2 md:mt-3 lg:mt-4 text-balance"
            data-testid="forgot-password_description"
          >
            {forgotPasswordMutation.isSuccess
              ? t("forgot_password.success_description")
              : t("forgot_password.description")}
          </p>
        )}
      </div>
      {isOnPremise ? (
        <h3
          className="text-xl md:text-md lg:text-1xl font-semibold text-center mt-3 md:mt-5 lg:mt-7"
          data-testid="forgot-password_on-premise_description"
        >
          {t("forgot_password.contact_system_administrator")}
        </h3>
      ) : (
        !forgotPasswordMutation.isSuccess && (
          <Form {...form}>
            <form
              onSubmit={handleSubmit}
              className="space-y-4 mt-6"
              data-testid="forgot-password_form"
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field, fieldState: { error } }) => (
                  <FormItem>
                    <FormLabel>{t("email.label")}</FormLabel>
                    <FormControl>
                      <StyledInput
                        className={AUTH_CONTROL_HEIGHT_CLASS}
                        data-testid="forgot-password_email-input"
                        placeholder={t("email.placeholder")}
                        {...field}
                        isError={!!error}
                      />
                    </FormControl>
                    <FormMessage data-testid="forgot-password_email-error" />
                  </FormItem>
                )}
              />
              <FormSubmitButton
                className={cn("w-full", AUTH_CONTROL_HEIGHT_CLASS)}
                isLoading={forgotPasswordMutation.isPending}
                data-testid="forgot-password_submit-button"
              >
                {t("forgot_password.submit")}
              </FormSubmitButton>
            </form>
          </Form>
        )
      )}
    </CenterCardLayout>
  );
};
export default ForgotPassword;
